import React, { useState } from "react";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";

const MAGENTA = "#9D2362";

const csvEscape = (s: string) =>
  /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;

export interface ExportMenuProps {
  /** Rows of the table to serialize; first row is the header. */
  getGrid: () => string[][];
  /** Base name for the CSV download (a trailing ".csv" is optional). */
  filename: string;
  /** Omitted => the "Print / Save as PDF" item is not rendered. */
  onPrint?: () => void;
  label?: string;
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
  disabled?: boolean;
}

/**
 * Reusable Export button: Copy table (TSV to clipboard) · Download CSV ·
 * Print / Save as PDF. Serialization matches `MetricTable`'s exporters so a
 * copied table and a downloaded CSV always agree.
 */
const ExportMenu: React.FC<ExportMenuProps> = ({
  getGrid,
  filename,
  onPrint,
  label = "Export",
  size = "medium",
  variant = "contained",
  disabled,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const close = () => setAnchor(null);

  const copyTable = async () => {
    close();
    const text = getGrid()
      .map((r) => r.join("\t"))
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setSnack("Table copied to clipboard");
    } catch {
      setSnack("Could not copy to clipboard");
    }
  };

  const downloadCsv = () => {
    close();
    const csv = getGrid()
      .map((r) => r.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.toLowerCase().endsWith(".csv")
      ? filename
      : `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const print = () => {
    close();
    if (onPrint) onPrint();
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        startIcon={<FileDownloadIcon />}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={
          variant === "contained"
            ? {
                backgroundColor: MAGENTA,
                "&:hover": { backgroundColor: "#7A1B4D" },
              }
            : { color: MAGENTA, borderColor: MAGENTA }
        }
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem onClick={copyTable}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy table</ListItemText>
        </MenuItem>
        <MenuItem onClick={downloadCsv}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
        {onPrint && (
          <MenuItem onClick={print}>
            <ListItemIcon>
              <PrintIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Print / Save as PDF</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Snackbar
        open={snack !== null}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </>
  );
};

export default ExportMenu;
